import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CreateSchemaVersionSchema, HttpProblemError, hasRole } from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDate, formatDateTime } from '../lib/format'
import { PageHeading } from '../components/PageHeading'

export function SchemasListPage() {
  const { t } = useTranslation()
  const auth = useAuth()
  const canEdit = hasRole(auth.user, 'developer')
  const schemas = useQuery({ queryKey: ['schemas'], queryFn: () => api().schemas.list() })

  return (
    <div>
      <PageHeading
        title={t('schemas.title')}
        description={t('schemas.description')}
        actions={
          canEdit ? (
            <Link to="/schemas/new" className="btn-primary">
              {t('schemas.newVersion')}
            </Link>
          ) : null
        }
      />
      {schemas.data && schemas.data.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900">
          <table className="min-w-full divide-y divide-stone-200 text-sm dark:divide-stone-700">
            <thead>
              <tr className="table-header">
                <th className="px-3 py-2">{t('schemas.name')}</th>
                <th className="px-3 py-2">{t('schemas.latest')}</th>
                <th className="px-3 py-2">{t('schemas.versions')}</th>
                <th className="px-3 py-2">{t('schemas.firstSeen')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {schemas.data.map((s) => (
                <tr key={s.name}>
                  <td className="px-3 py-2 font-mono">
                    <Link className="hover:underline" to={`/schemas/${s.name}`}>
                      {s.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{s.latestVersion}</td>
                  <td className="px-3 py-2">
                    {s.versions}
                    {s.anyDeprecated ? (
                      <span className="ml-1 text-xs text-amber-700 dark:text-amber-400">
                        {t('schemas.someDeprecated')}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-stone-600 dark:text-stone-400">
                    {formatDate(s.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-stone-500 dark:text-stone-400">{t('schemas.empty')}</p>
      )}
    </div>
  )
}

export function SchemaVersionsPage() {
  const { t } = useTranslation()
  const { name } = useParams<{ name: string }>()
  const versions = useQuery({
    queryKey: ['schema', name],
    queryFn: () => api().schemas.versions(name!),
    enabled: Boolean(name),
  })
  const queryClient = useQueryClient()
  const auth = useAuth()
  const canEdit = hasRole(auth.user, 'developer')

  const deprecate = useMutation({
    mutationFn: ({ version, deprecated }: { version: number; deprecated: boolean }) =>
      api().schemas.deprecate(name!, version, deprecated),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['schema', name] })
      void queryClient.invalidateQueries({ queryKey: ['schemas'] })
    },
  })

  return (
    <div>
      <PageHeading
        title={t('schemas.schemaTitle', { name: name ?? '' })}
        description={t('schemas.versionListDescription')}
        actions={
          canEdit ? (
            <Link
              to={`/schemas/new?name=${encodeURIComponent(name ?? '')}`}
              className="btn-primary"
            >
              {t('schemas.addVersion')}
            </Link>
          ) : null
        }
      />
      {versions.data?.map((v) => (
        <div key={v.id} className="card mb-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-base font-medium">
                <span className="font-mono">
                  {v.name}@{v.version}
                </span>
                {v.deprecated ? (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    {t('schemas.deprecated')}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-stone-500 dark:text-stone-400">
                {t('schemas.registeredAt', { date: formatDateTime(v.createdAt) })}
              </div>
            </div>
            {canEdit ? (
              <button
                className="btn-secondary"
                onClick={() => {
                  deprecate.mutate({ version: v.version, deprecated: !v.deprecated })
                }}
              >
                {v.deprecated ? t('schemas.undeprecate') : t('schemas.deprecate')}
              </button>
            ) : null}
          </div>
          <pre className="mt-3 max-h-64 overflow-auto rounded bg-stone-50 p-3 text-xs dark:bg-stone-950 dark:text-stone-200">
            {JSON.stringify(v.jsonSchema, null, 2)}
          </pre>
        </div>
      ))}
    </div>
  )
}

export function NewSchemaPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const queryParams = new URLSearchParams(window.location.search)
  const [name, setName] = useState(queryParams.get('name') ?? 'tccc_card')
  const [version, setVersion] = useState('1')
  const [schema, setSchema] = useState<Record<string, unknown> | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = async (file: File) => {
    setError(null)
    const text = await file.text()
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch (err) {
      setError(t('schemas.jsonInvalid', { error: (err as Error).message }))
      return
    }
    setSchema(parsed as Record<string, unknown>)
    setFileName(file.name)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!schema) {
      setError(t('schemas.noFile'))
      return
    }
    const dto = {
      name,
      version: Number.parseInt(version, 10),
      jsonSchema: schema,
    }
    const valid = CreateSchemaVersionSchema.safeParse(dto)
    if (!valid.success) {
      setError(valid.error.issues.map((i) => i.message).join('; '))
      return
    }
    try {
      await api().schemas.create(valid.data)
      void queryClient.invalidateQueries({ queryKey: ['schemas'] })
      void queryClient.invalidateQueries({ queryKey: ['schema', name] })
      void navigate(`/schemas/${name}`)
    } catch (err) {
      setError(
        err instanceof HttpProblemError
          ? (err.problem.detail ?? err.problem.title)
          : t('common.unexpectedError'),
      )
    }
  }

  return (
    <div>
      <PageHeading title={t('schemas.registerTitle')} description={t('schemas.immutable')} />
      <form
        onSubmit={(e) => {
          void submit(e)
        }}
        className="card space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">{t('schemas.name')}</label>
            <input
              className="input font-mono"
              value={name}
              onChange={(e) => {
                setName(e.target.value.toLowerCase())
              }}
              placeholder="tccc_card"
            />
          </div>
          <div>
            <label className="label">{t('schemas.versionLabel')}</label>
            <input
              className="input"
              type="number"
              min={1}
              value={version}
              onChange={(e) => {
                setVersion(e.target.value)
              }}
            />
          </div>
        </div>
        <div>
          <label className="label">{t('schemas.jsonSchemaFile')}</label>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFile(file)
            }}
          />
          <button type="button" className="btn-secondary" onClick={() => fileRef.current?.click()}>
            {t('schemas.chooseFile')}
          </button>
          {fileName ? (
            <span className="ml-3 text-sm text-stone-600 dark:text-stone-400">{fileName}</span>
          ) : null}
        </div>
        {schema ? (
          <pre className="max-h-64 overflow-auto rounded bg-stone-50 p-3 text-xs font-mono dark:bg-stone-950 dark:text-stone-200">
            {JSON.stringify(schema, null, 2)}
          </pre>
        ) : null}
        {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void navigate(-1)
            }}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn-primary">
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  )
}
